/**
 * useCachedResource — cache-first data loading for admin/dashboard surfaces.
 *
 * The rule (product owner): once something has loaded, KEEP showing it — never flash
 * a spinner or reload just because a tab was re-opened. Revalidate quietly in the
 * background and only swap the data in if it actually CHANGED on the cloud, and poll
 * every ~60s so a real cloud change appears within a minute. Mutations on our side
 * push the fresh value straight into the cache (call `revalidate()` after a save).
 *
 * Backed by the same localStorage cache as the rest of the app (stores/cache.ts), so
 * the data also survives a full reload, not just a tab switch.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { cacheEpoch, readCacheEntry, writeCache } from "./cache.js";

/**
 * GEN-8: "we are showing you a cached copy, and here is why it hasn't refreshed."
 *
 * Stale-while-revalidate is correct — silently passing a up-to-7-day-old copy off
 * as live is not. An expired token / changed role / dead backend used to be
 * completely invisible: the fetch threw, the error was swallowed because SOMETHING
 * was cached, and the poll kept firing into the void every 60s.
 */
export interface Staleness {
  /** When the copy currently on screen was last confirmed fresh (epoch ms, null if unknown). */
  cachedAt: number | null;
  /** Why the most recent refresh failed. */
  reason: string;
}

export interface CachedResource<T> {
  /** Last-known value (from cache instantly, then revalidated). */
  data: T | null;
  /** True only on the very first load with an empty cache. */
  loading: boolean;
  /** Set only when there's no cached value to fall back on. */
  error: string | null;
  /**
   * Non-null while cached data is on screen but the last refresh FAILED. Render
   * it (see `admin/StaleNotice`) — this is the only signal the operator gets that
   * what they're looking at is not live.
   */
  stale: Staleness | null;
  /** Force a background revalidation (e.g. right after a save). */
  revalidate: () => Promise<void>;
  /** Optimistically set + cache the value (after a local mutation). */
  set: (value: T) => void;
}

export function useCachedResource<T>(
  cacheKey: string,
  fetcher: () => Promise<T>,
  opts: { pollMs?: number; enabled?: boolean } = {},
): CachedResource<T> {
  const { pollMs = 60_000, enabled = true } = opts;
  const [data, setData] = useState<T | null>(() => readCacheEntry<T>(cacheKey)?.data ?? null);
  const [loading, setLoading] = useState(data == null);
  const [error, setError] = useState<string | null>(null);
  const [stale, setStale] = useState<Staleness | null>(null);

  // Keep the latest fetcher without making it a dependency (callers pass inline fns).
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  // Mirrors of the current value / its age, kept in refs so `revalidate` can read
  // them WITHOUT doing work inside a `setState` updater. Updaters must be pure —
  // StrictMode double-invokes them, and the old code called `setError` from inside
  // one (which fired twice and made the error depend on render mode).
  const dataRef = useRef<T | null>(data);
  const freshAtRef = useRef<number | null>(readCacheEntry<T>(cacheKey)?.at ?? null);
  // The cache epoch the on-screen value belongs to; a sign-out bumps the global one (AR-08).
  const epochRef = useRef(cacheEpoch());
  const mountedRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const revalidate = useCallback(async () => {
    if (!enabled) return;
    const epoch = cacheEpoch();
    const superseded = () => !mountedRef.current || epoch !== cacheEpoch();
    try {
      const fresh = await fetcherRef.current();
      if (superseded()) return;
      epochRef.current = epoch;
      // Only re-render + re-cache when the value actually changed (avoids the
      // "reloads every time" flicker and needless work on each tab switch).
      if (JSON.stringify(dataRef.current) !== JSON.stringify(fresh)) {
        dataRef.current = fresh;
        writeCache(cacheKey, fresh, epoch);
        setData(fresh);
      }
      // A successful round-trip re-confirms the displayed copy even when it didn't
      // change — a ref, so the common "nothing changed" poll re-renders nothing.
      freshAtRef.current = Date.now();
      setError(null);
      setStale(null);
    } catch (e) {
      if (superseded()) return;
      const reason = e instanceof Error ? e.message : String(e);
      // Offline / expired token / 403: keep showing the cached value, but SAY SO.
      // `error` still means "nothing to show at all" so existing callers are unchanged.
      if (dataRef.current == null) setError(reason);
      else setStale({ cachedAt: freshAtRef.current, reason });
    } finally {
      setLoading(false);
    }
  }, [cacheKey, enabled]);

  useEffect(() => {
    if (!enabled) return;
    void revalidate();
    if (!pollMs) return;
    const id = setInterval(() => { void revalidate(); }, pollMs);
    return () => clearInterval(id);
  }, [revalidate, pollMs, enabled]);

  const set = useCallback((value: T) => {
    if (!mountedRef.current) return;
    // A value computed before a sign-out is the outgoing account's: refetch instead of caching it.
    if (epochRef.current !== cacheEpoch()) { void revalidate(); return; }
    writeCache(cacheKey, value, epochRef.current);
    dataRef.current = value;
    freshAtRef.current = Date.now();
    setData(value);
    setStale(null);
  }, [cacheKey, revalidate]);

  return { data, loading, error, stale, revalidate, set };
}
